import React from 'react';
import { FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import { equipmentQrImageUrl } from '../utils/equipmentQr';
import '../styles/InventoryEquipment.css';

function formatDate(raw) {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  });
}

function statusLabel(status) {
  const s = String(status || '');
  if (s === 'InUse') return 'In Use';
  if (s === 'InService') return 'In Service';
  if (s === 'ServiceOverdue') return 'Service Overdue';
  return s || '—';
}

function statusClass(status) {
  const s = String(status || '');
  if (s === 'InUse') return 'ieq-pill--inuse';
  if (s === 'InService') return 'ieq-pill--inservice';
  if (s === 'ServiceOverdue') return 'ieq-pill--overdue';
  return 'ieq-pill--active';
}

function isOverdue(row) {
  if (String(row?.status) === 'ServiceOverdue') return true;
  if (!row?.nextServiceDate) return false;
  const d = new Date(row.nextServiceDate);
  return !Number.isNaN(d.getTime()) && d < new Date();
}

export default function EquipmentDetailModal({
  equipment,
  onClose,
  onEdit,
}) {
  if (!equipment) return null;

  const overdue = isOverdue(equipment);
  const inService = String(equipment.status) === 'InService';
  const qrUrl = equipmentQrImageUrl(equipment.assetNumber || equipment.id);
  const brandModel =
    [equipment.brand, equipment.model].filter(Boolean).join(' ') ||
    equipment.brandModel;

  return (
    <div className="ieq-overlay" role="dialog" aria-modal="true">
      <div className="ieq-modal ieq-modal--detail">
        <header className="ieq-modal-header">
          <h2>Equipment — {equipment.assetNumber}</h2>
          <button
            type="button"
            className="ieq-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </header>

        <div className="ieq-modal-body">
          <div className="ieq-badges">
            <span className="ieq-code-badge">{equipment.assetNumber}</span>
            <span className={`ieq-pill ${statusClass(equipment.status)}`}>
              {statusLabel(equipment.status)}
            </span>
          </div>

          <h3 className="ieq-title">
            {brandModel}
            {equipment.equipmentType ? ` — ${equipment.equipmentType}` : ''}
          </h3>

          <div className="ieq-detail-cards">
            <div className="ieq-detail-card">
              <b>Equipment Type</b>
              <span>{equipment.equipmentType || '—'}</span>
            </div>
            <div className="ieq-detail-card">
              <b>Brand / Model</b>
              <span>{brandModel || '—'}</span>
            </div>
            <div className="ieq-detail-card">
              <b>Purchase Date</b>
              <span>{formatDate(equipment.purchaseDate)}</span>
            </div>
            <div className="ieq-detail-card">
              <b>Purchase Value</b>
              <span>{formatMoney(equipment.purchaseValue)}</span>
            </div>
            <div className="ieq-detail-card">
              <b>Assigned To</b>
              <span>{equipment.assignedTo || '—'}</span>
            </div>
            <div className="ieq-detail-card">
              <b>Current Site</b>
              <span>{equipment.currentSite || equipment.currentLocation || '—'}</span>
            </div>
            <div className="ieq-detail-card">
              <b>Last Service</b>
              <span>{formatDate(equipment.lastServiceDate)}</span>
            </div>
            <div className="ieq-detail-card">
              <b>Next Service Due</b>
              <span>{formatDate(equipment.nextServiceDate)}</span>
            </div>
          </div>

          {overdue && (
            <div className="ieq-alert ieq-alert--warn">
              <FaExclamationTriangle />
              <span>
                Service is <strong className="ieq-alert-strong">overdue</strong>.
                Next service due: {formatDate(equipment.nextServiceDate)}.
              </span>
            </div>
          )}
          {!overdue && inService && (
            <div className="ieq-alert ieq-alert--info">
              <FaExclamationTriangle />
              <span>
                Service is <strong>currently scheduled</strong>. Next service due:{' '}
                {formatDate(equipment.nextServiceDate)}.
              </span>
            </div>
          )}

          <div className="ieq-qr">
            <img src={qrUrl} alt={`QR ${equipment.assetNumber}`} />
            <span>QR Code: {equipment.assetNumber}</span>
            <small>
              Print this code and stick it on the machine. Scanning it opens this
              equipment record.
            </small>
          </div>

          <div className="ieq-modal-footer">
            <button type="button" className="ieq-btn" onClick={onClose}>
              Close
            </button>
            <button
              type="button"
              className="ieq-btn ieq-btn--primary"
              onClick={() => onEdit?.(equipment)}
            >
              Edit Equipment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
